/**
 * Die Geräte hinter einer Zahl des Dashboards (Rückmeldungen 8.1).
 *
 * Ein Tipp auf „Nicht einsatzbereit“ zeigt genau diese Geräte. Gerechnet wird
 * aus der lokalen Kopie, also auch offline, nach denselben Regeln wie die
 * Kennzahlen des Servers: Archiviertes zählt nicht zum Bestand.
 */

import type { Artikel, Verbleib, Zustand } from "@thw/contracts";
import { pruefAmpel, statusRegeln, zustandOhneBuchung, type PruefKlasse } from "@thw/core-domain";
import type { Bestand } from "./bestand.js";

/** Frist für „bald fällig“, wie beim Server (KONZEPT.md §14.2). */
export const PRUEF_FRIST_TAGE = 30;

export interface ListenFilter {
  status?: string;
  verbleib?: Verbleib;
  einsatzbereit?: boolean;
  verfuegbar?: boolean;
  pruefung?: PruefKlasse;
}

const VERBLEIBE: readonly Verbleib[] = ["nicht_erfasst", "im_lager", "im_einsatz", "ausgeliehen"];
const PRUEFKLASSEN: readonly PruefKlasse[] = [
  "ueberfaellig",
  "bald_faellig",
  "in_ordnung",
  "ohne_termin",
];

/** Der Filter aus der Adresse -- was nicht passt, fällt weg. */
export function filterAus(parameter: URLSearchParams): ListenFilter {
  const filter: ListenFilter = {};
  const status = parameter.get("status");
  if (status !== null && status !== "") filter.status = status;
  const verbleib = parameter.get("verbleib");
  if (VERBLEIBE.includes(verbleib as Verbleib)) filter.verbleib = verbleib as Verbleib;
  const bereit = parameter.get("einsatzbereit");
  if (bereit === "ja" || bereit === "nein") filter.einsatzbereit = bereit === "ja";
  if (parameter.get("verfuegbar") === "ja") filter.verfuegbar = true;
  const pruefung = parameter.get("pruefung");
  if (PRUEFKLASSEN.includes(pruefung as PruefKlasse)) filter.pruefung = pruefung as PruefKlasse;
  return filter;
}

export function filtern(
  bestand: Bestand,
  filter: ListenFilter,
  heute: string = new Date().toISOString().slice(0, 10),
): { artikel: Artikel; zustand: Zustand }[] {
  const treffer: { artikel: Artikel; zustand: Zustand }[] = [];
  // Nie gebucht: noch nicht erfasst, mit dem Standardstatus -- wie beim Server.
  const ohneBuchung = zustandOhneBuchung(statusRegeln(bestand.statusArten));
  for (const artikel of bestand.artikel.values()) {
    const zustand = bestand.zustaende.get(artikel.id) ?? ohneBuchung;
    if (zustand.archiviert) continue;
    if (filter.status !== undefined && zustand.status !== filter.status) continue;
    if (filter.verbleib !== undefined && zustand.verbleib !== filter.verbleib) continue;
    if (filter.einsatzbereit !== undefined && zustand.einsatzbereit !== filter.einsatzbereit) {
      continue;
    }
    if (filter.verfuegbar === true && !zustand.verfuegbar) continue;
    if (
      filter.pruefung !== undefined &&
      pruefAmpel(artikel.pruefungen, heute, PRUEF_FRIST_TAGE).klasse !== filter.pruefung
    ) {
      continue;
    }
    treffer.push({ artikel, zustand });
  }
  return treffer.sort((a, b) => a.artikel.bezeichnung.localeCompare(b.artikel.bezeichnung, "de"));
}

const VERBLEIB_TITEL: Record<Verbleib, string> = {
  nicht_erfasst: "Noch nicht erfasst",
  im_lager: "Im Lager",
  im_einsatz: "Im Einsatz",
  ausgeliehen: "Ausgeliehen",
};

const PRUEF_TITEL: Record<PruefKlasse, string> = {
  ueberfaellig: "Prüfung überfällig",
  bald_faellig: `Prüfung in ${String(PRUEF_FRIST_TAGE)} Tagen fällig`,
  in_ordnung: "Prüfung in Ordnung",
  ohne_termin: "Ohne Prüftermin",
};

/** Die Überschrift der Liste: „Nicht einsatzbereit“, „Status: Defekt“. */
export function titelFuer(filter: ListenFilter, bestand: Bestand): string {
  const teile: string[] = [];
  if (filter.verfuegbar === true) teile.push("Greifbar");
  if (filter.einsatzbereit !== undefined) {
    teile.push(filter.einsatzbereit ? "Einsatzbereit" : "Nicht einsatzbereit");
  }
  if (filter.status !== undefined) {
    const art = bestand.statusArten.find((a) => a.schluessel === filter.status);
    teile.push(`Status: ${art?.bezeichnung ?? filter.status}`);
  }
  if (filter.verbleib !== undefined) teile.push(VERBLEIB_TITEL[filter.verbleib]);
  if (filter.pruefung !== undefined) teile.push(PRUEF_TITEL[filter.pruefung]);
  return teile.length === 0 ? "Bestand" : teile.join(" · ");
}
